import { PERISCOPE_TAB_TYPES } from '@sonar-software/sonar-ts-constants';
import { picklistSortOptions } from './PicklistSortOptions';
import { SelectionTable } from '@ui/tables';
import {
	ApiCodeToggleQueryParams,
	SearchFilterQueryParams,
	SortByFilterQueryParams,
	TagFilterQueryParams
} from '../filters';
import {
	getRowKey,
	paginationDefaultQueryParams,
	paginationFallbackQueryParams,
	PaginationQueryParams,
	PeriscopePagination
} from '..';
import { OffsetPaginatedResource, PeriscopeSfPicklistValue, SonarObjectType } from '@sonar-software/sonar-ts-types';
import { PeriscopePicklistValue, PicklistValueSearch } from '.';
import React, { useCallback, useRef } from 'react';
import { usePeriscopeActions, useQueryResource, useSelectionTable } from '@hooks';

type Resource = OffsetPaginatedResource<PeriscopeSfPicklistValue>;
export type SalesforcePicklistSearchableSelectionTableQueryParams = ApiCodeToggleQueryParams &
	PaginationQueryParams &
	SearchFilterQueryParams &
	SortByFilterQueryParams &
	TagFilterQueryParams;

export interface SalesforcePicklistSearchableSelectionTableProps {
	parentIndex: number;
	resourceIdsUri: string;
	uri: string;
}

export const SalesforcePicklistSearchableSelectionTable: React.FC<SalesforcePicklistSearchableSelectionTableProps> = ({
	parentIndex,
	resourceIdsUri,
	uri
}) => {
	const scrollableListRef = useRef<HTMLDivElement>(null);

	const { loading, resource, queryParams, updateParams } = useQueryResource<
		Resource,
		SalesforcePicklistSearchableSelectionTableQueryParams
	>({
		defaultParams: {
			...paginationDefaultQueryParams,
			sortBy: picklistSortOptions[ 0 ].key,
			sortDirection: picklistSortOptions[ 0 ].direction
		},
		fallbackParams: paginationFallbackQueryParams,
		uri
	});

	const selectionData = useSelectionTable({
		getRowKey,
		queryParams,
		resourceIdsUri,
		results: resource?.results,
		selectionType: SonarObjectType.SalesforcePicklistValue
	});

	const { openPeriscope } = usePeriscopeActions();

	const handlePicklistValueClick = useCallback(
		(picklistValue: PeriscopeSfPicklistValue) => {
			const { displayName, sonarObjectId } = picklistValue;

			openPeriscope({
				id: sonarObjectId,
				name: displayName,
				openedFromIndex: parentIndex,
				type: SonarObjectType.SalesforcePicklistValue
			});
		},
		[ openPeriscope, parentIndex ]
	);

	const renderPicklistValue = useCallback(
		(picklistValue: PeriscopeSfPicklistValue) =>
			<PeriscopePicklistValue
				key={ picklistValue.sonarObjectId }
				onPicklistValueClick={ handlePicklistValueClick }
				picklistValue={ picklistValue } />
		,
		[ handlePicklistValueClick ]
	);

	const totalCount = resource?.totalCount;
	const results = resource?.results;

	return (
		<>
			<PicklistValueSearch onChange={ updateParams } />
			<SelectionTable
				dataSource={ results }
				loading={ loading }
				ref={ scrollableListRef }
				renderRow={ renderPicklistValue }
				rowKey={ getRowKey }
				selectionData={ selectionData }
				selectionType={ PERISCOPE_TAB_TYPES.PICKLIST_VALUES } />
			<PeriscopePagination
				loading={ loading }
				onChange={ updateParams }
				queryParams={ queryParams }
				scrollRef={ scrollableListRef }
				totalCount={ Number(totalCount) } />
		</>
	);
};
